import { DEFAULT_MAX_FOLLOWING_TURNS, DEFAULT_MAX_TURNS_WITHOUT_ACTION, DEFAULT_TIME_PER_TURN, GAME_LENGTHS, GAME_MODES, WINNING_TYPES } from "../consts/game.js";
import { PIECES, STANDARD_PLAYER_PIECE_MAP } from "../consts/pieces.js";
import { decodePosition, encodePosition } from "../codec/position.js";
import match from "../utils/match.js";
import remember from "../utils/remember.js";
import GameBoard from "./game-board.js";

export default class BlindKingsGame {

    constructor({
        // PLAYERS
        firstPlayer,
        secondPlayer,
        // GAME
        turnNumber = 1,
        board,
        graveyard = [],
        following = {},
        turnsWithoutAction = 0,
        lastActionPlayerIndex,
        lastAction,
        firstPlayerQuit,
        secondPlayerQuit,
        timeFinished,
        turnFinishBy,

        // OPTIONS
        gameMode = GAME_MODES.LOCAL_GAME,
        maxTurnsWithoutAction = DEFAULT_MAX_TURNS_WITHOUT_ACTION,
        maxFollowingTurns = DEFAULT_MAX_FOLLOWING_TURNS,
        turnLength = DEFAULT_TIME_PER_TURN,
        // USER (Player or Spectator)
        userPlayerId,
    }) {
        this.firstPlayer = firstPlayer;
        this.secondPlayer = secondPlayer;
        this.board = board;
        this.graveyard = graveyard;
        this.turnNumber = turnNumber;
        this.turnsWithoutAction = turnsWithoutAction;
        this.following = following;
        this.lastActionPlayerIndex = lastActionPlayerIndex;
        this.lastAction = lastAction;
        this.firstPlayerQuit = firstPlayerQuit;
        this.secondPlayerQuit = secondPlayerQuit;
        this.timeFinished = timeFinished;
        this.turnFinishBy = turnFinishBy;

        // OPTIONS
        this.gameMode = gameMode;
        this.maxTurnsWithoutAction = +maxTurnsWithoutAction;
        this.maxFollowingTurns = +maxFollowingTurns;
        this.turnLength = turnLength;
        this.userPlayerId = userPlayerId;
    }

    get userIsFirstPlayer() { return this.userPlayerId === this.firstPlayer?.playerId; }
    get userIsSecondPlayer() { return this.userPlayerId === this.secondPlayer?.playerId; }

    get userIsInGame() { return this.userIsFirstPlayer || this.userIsSecondPlayer; }

    get userWon() {
        if (!this.isGameOver || !this.userIsInGame) return false;
        if (this.userIsFirstPlayer) return this.firstPlayerWon;
        return !this.firstPlayerWon;
    }

    get gameBoard() { return remember(this, `__gameBoard`, () => new GameBoard({ board: this.board, blindKingsGame: this })); }

    get turnIndex() { return this.turnNumber % 2; }

    get turnPlayer() {
        if (this.turnIndex) return this.secondPlayer;
        return this.firstPlayer;
    }

    get lastActionPlayer() {
        if (this.lastActionPlayerIndex) return this.firstPlayer;
        return this.secondPlayer;
    }

    get isUserTurn() {
        const {
            turnPlayer: {
                playerId: turnPlayerId,
            } = {},
            userPlayerId,
        } = this;

        return userPlayerId === turnPlayerId;
    }

    get isFirstPlayersTurn() { return this.turnPlayer === this.firstPlayer; }
    get isSecondPlayersTurn() { return this.turnPlayer === this.secondPlayer; }

    get completeGamePieceList() {
        return remember(this, `__completeGamePieceList`, () => (
            Array(2).fill().reduce((all, _, playerIndex) => [
                ...all,
                ...Object.entries(STANDARD_PLAYER_PIECE_MAP).reduce((all, [pieceName, pieceAmount]) => [
                    ...all,
                    ...Array(pieceAmount).fill({
                        ...PIECES[pieceName],
                        teamIndex: playerIndex,
                    }),
                ], []),
            ], [])
        ));
    }

    get allActiveBoardPieces() { return this.gameBoard.allActiveBoardPieces; }

    get followingTooLongMap() {
        // returns a map of pieces that have max following and what id
        return remember(this, `__followingTooLong`, () => {
            const {
                following = {},
            } = this;


            const allFollowing = Object.values(following);

            return allFollowing.reduce((all, { pieceId, following = {} }) => {
                // Checks to see if any of the following are max followed
                const maxFollowing = Object.entries(following).find(([_, followingTurns]) => followingTurns === this.maxFollowingTurns);

                if (maxFollowing) {
                    const [otherPieceId] = maxFollowing;
                    return {
                        ...all,
                        [pieceId]: +otherPieceId,
                    }
                }
                return all;
            }, {});
        });
    }

    getPlayerFollowing = playerIndex => {
        return this.following[playerIndex];
    }

    get allPotentialUnturnedPieces() {
        return remember(this, `__allPotentialUnturnedPieces`, () => {
            const {
                allNonGraveyardPieces,
                allActiveBoardPieces,
            } = this;

            const allPotentialUnturnedPieces = [...allNonGraveyardPieces];
            allActiveBoardPieces.forEach(activeBoardPiece => {
                const startIndex = allPotentialUnturnedPieces.findIndex(({ value, teamIndex }) => (
                    (teamIndex === activeBoardPiece.teamIndex)
                    &&
                    (value === activeBoardPiece.value)
                ));
                allPotentialUnturnedPieces.splice(startIndex, 1);
            });
            return allPotentialUnturnedPieces;
        });
    }

    getPlayerUnturnedPieces = playerIndex => remember(this, `__playerUnturnedPieces<${playerIndex}>`, () => (
        this.allPotentialUnturnedPieces.filter(({ teamIndex }) => playerIndex === teamIndex)
    ));

    getPlayerGraveyard = playerIndex => remember(this, `__graveyardPieces<${playerIndex}>`, () => (
        this.graveyard.filter(piece => piece.teamIndex === playerIndex)
    ));

    getPlayerGraveyardPieceCount = playerIndex => remember(this, `__getPlayerGraveyardPieceCount<${playerIndex}>`, () => (
        this.getPlayerGraveyard(playerIndex).reduce((graveyardPieceValueCount, { value }) => ({
            ...graveyardPieceValueCount,
            [value]: (graveyardPieceValueCount[value] || 0) + 1,
        }), {})
    ));

    get allNonGraveyardPieces() {
        return remember(this, `__allNonGraveyardPieces`, () => {
            const {
                completeGamePieceList,
                graveyard,
            } = this;

            let allNonGraveyardPieces = [...completeGamePieceList];
            graveyard.forEach(deadPiece => {
                const startIndex = allNonGraveyardPieces.findIndex(({ value, teamIndex }) => (
                    (teamIndex === deadPiece.teamIndex)
                    &&
                    (value === deadPiece.value)
                ));

                allNonGraveyardPieces.splice(startIndex, 1);
            });
            return allNonGraveyardPieces;
        });
    }

    getPlayerNonGraveyardPieces = playerIndex => this.allNonGraveyardPieces.filter(({ teamIndex }) => teamIndex === playerIndex)

    getPlayerCanTakeTurn = playerIndex => remember(this, `__playerCanTakeTurn<${playerIndex}>`, () => (
        this.gameBoard.availableMovesByTeamIndex(playerIndex).length
    ))

    get isGameOver() {
        const armySize = this.completeGamePieceList.length / 2;
        return (
            this.timeFinished
            ||
            this.firstPlayerQuit
            ||
            this.secondPlayerQuit
            ||
            this.turnsWithoutAction > this.maxTurnsWithoutAction
            ||
            !this.getPlayerCanTakeTurn(this.turnIndex)
            // Wiped army ends the match immediately — do not wait for that seat's turn.
            ||
            this.getPlayerGraveyard(0).length >= armySize
            ||
            this.getPlayerGraveyard(1).length >= armySize
        );
    }

    getPlayerLostByNormalMeans = playerIndex => (
        // once all the player pieces have been removed from the game
        this.getPlayerGraveyard(playerIndex).length === (this.completeGamePieceList.length / 2)
        ||
        // The player cannot move
        (
            (this.turnIndex === playerIndex)
            &&
            !this.getPlayerCanTakeTurn(playerIndex)
        )
    );

    get playerThatLostByNormalMeans() { return [this.firstPlayer, this.secondPlayer].find((_, playerIndex) => this.getPlayerLostByNormalMeans(playerIndex)); }

    getPlayerActivePieces = playerIndex => this.gameBoard.getActivePiecesByTeamIndex(playerIndex);

    get firstPlayerActivePieces() {
        return this.getPlayerActivePieces(0);
    }
    get secondPlayerActivePieces() {
        return this.getPlayerActivePieces(1);
    }

    getPlayerHighestPiece = playerIndex => this.getPlayerNonGraveyardPieces(playerIndex).reduce((highestPiece, piece) => piece.value > highestPiece.value ? piece : highestPiece, PIECES.Empty)

    get firstPlayerHighestPiece() {
        return this.getPlayerHighestPiece(0);
    }

    get secondPlayerHighestPiece() {
        return this.getPlayerHighestPiece(1);
    }

    get winningType() {
        const {
            isGameOver,
            timeFinished,
            firstPlayerQuit,
            secondPlayerQuit,
            playerThatLostByNormalMeans,
            firstPlayerActivePieces,
            secondPlayerActivePieces,
            firstPlayerHighestPiece,
            secondPlayerHighestPiece,
        } = this;

        if (!isGameOver) return undefined;
        if (timeFinished) return WINNING_TYPES.OVER_TIME;
        if (firstPlayerQuit || secondPlayerQuit) return WINNING_TYPES.USER_QUIT;
        if (playerThatLostByNormalMeans) return WINNING_TYPES.FULL_GAME;
        if (firstPlayerActivePieces?.length !== secondPlayerActivePieces?.length) return WINNING_TYPES.MORE_PIECES;
        if (firstPlayerHighestPiece?.value !== secondPlayerHighestPiece?.value) return WINNING_TYPES.HIGHEST_PIECE;
        return WINNING_TYPES.LAST_ACTION;
    }

    get gameWinner() {
        const {
            playerThatLostByNormalMeans,
            timeFinished,
            firstPlayerQuit,
            firstPlayerActivePieces,
            secondPlayerActivePieces,
            firstPlayerHighestPiece: {
                value: firstPlayerHighestPieceValue,
            } = {},
            secondPlayerHighestPiece: {
                value: secondPlayerHighestPieceValue,
            } = {},
            firstPlayer,
            secondPlayer,
            lastActionPlayer,
        } = this;


        return match(this.winningType)
            .equals(WINNING_TYPES.OVER_TIME, this.isFirstPlayersTurn ? this.secondPlayer : this.firstPlayer)
            .equals(WINNING_TYPES.USER_QUIT, firstPlayerQuit ? secondPlayer : firstPlayer)
            .equals(WINNING_TYPES.FULL_GAME, firstPlayer === playerThatLostByNormalMeans ? secondPlayer : firstPlayer)
            .equals(WINNING_TYPES.MORE_PIECES, firstPlayerActivePieces.length > secondPlayerActivePieces.length ? firstPlayer : secondPlayer)
            .equals(WINNING_TYPES.HIGHEST_PIECE, firstPlayerHighestPieceValue > secondPlayerHighestPieceValue ? firstPlayer : secondPlayer)
            .equals(WINNING_TYPES.LAST_ACTION, lastActionPlayer)
            .otherwise();
    }

    get firstPlayerWon() {
        return this.gameWinner === this.firstPlayer;
    }

    get gameLength() {
        if (this.turnNumber < 50) return GAME_LENGTHS.TOO_SHORT;
        if (this.turnNumber < 100) return GAME_LENGTHS.SHORT_GAME;
        if (this.turnNumber < 350) return GAME_LENGTHS.MEDIUM_GAME;
        return GAME_LENGTHS.LONG_GAME;
    }


    get encodedGameState() {
        return encodePosition({
            turnNumber: this.turnNumber,
            board: this.board,
            graveyard: this.graveyard,
            following: this.following,
            turnsWithoutAction: this.turnsWithoutAction,
            lastActionPlayerIndex: this.lastActionPlayerIndex,
        });
    }

    static decodeGameState(encodedGameState) {
        return decodePosition(encodedGameState);
    }
}
